import {inject, Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {CmsApiService} from '../cms-api.service';
import {ProgressDialogComponent} from '../../shared/progress-dialog/progress-dialog.component';
import {AppNotification, NotificationService} from '../../shared/notification.service';
import {Image} from '../definitions/image';
import User from '../../administration/admin-user/User';
import {ImageItem} from '../definitions/image-item';
import {ContextList} from '../definitions/context-list';
import {SuperObjectModel} from '../definitions/super-object-model';
import {UrlData} from '../definitions/url-data';
import {GetArtifactParams} from '../definitions/get-artifact-params';
import {LoggerService} from "../logger.service";
import {TranslateService} from "@ngx-translate/core";
import {CrudService} from "../crud.service";
import {ObjectTypes} from "../definitions/object-types";
import {MediaItem} from "../definitions/media-item";
import {HttpClient} from "@angular/common/http";
import {UploadOptions, UploadResult, UploadType} from "./upload.types";
import {
  contentTypeForUpload,
  getUploadUrls,
  MAX_UPLOAD_FILE_BYTES,
  prepareFileForUpload,
  readFileAsArrayBuffer,
  snapshotFiles,
} from "./upload.helpers";
import {createUploadWorker} from './upload-worker.factory';
import {uploadMultipart} from './multipart-upload';

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly crud: CrudService = inject(CrudService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly modalService: MatDialog = inject(MatDialog);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly translate: TranslateService = inject(TranslateService);

  private uploadWorker: Worker;
  private uploadWorkerCallback: any;
  private lastUploadWorkerResult: UploadResult;
  private lastMediaFolder: SuperObjectModel;

  async uploadFiles(files: File[] | FileList, uploadType: UploadType, parentObject: SuperObjectModel): Promise<MediaItem[]> {
    const res = [];
    // Snapshot immediately — FileList from mobile pickers can empty out after awaits.
    const fileList = snapshotFiles(files);
    if (fileList.length) {
      const progressModal = this.modalService.open(ProgressDialogComponent, {
        data: {
          descriptorText: fileList.length > 1 ? 'TRANS__IMAGE_SELECTOR__PREPARING_UPLOAD_COUNT' : 'TRANS__IMAGE_SELECTOR__PREPARING_UPLOAD',
          count: fileList.length
        },
        disableClose: true,
        panelClass: 'progress-modal'
      });
      for (const originalFile of fileList) {
        if (originalFile.size > MAX_UPLOAD_FILE_BYTES) {
          this.notificationService.addNotification(new AppNotification(
            ['TRANS__EXCEPTION_MULTIMEDIA_FILE_TOO_LARGE', ': ', originalFile.name], 'error'));
          continue;
        }
        let file: File;
        try {
          // Materialize bytes before ordering the upload URL — that await is long
          // enough for iOS/Android to revoke the original camera/gallery File.
          file = await prepareFileForUpload(originalFile, uploadType);
        } catch (e) {
          this.logger.error(`Preparing file for upload failed: ${e}`);
          this.notificationService.addNotification(new AppNotification(
            ['TRANS__UPLOAD_SERVICE__UPLOAD_ERROR', ': ', originalFile?.name || ''], 'error'));
          continue;
        }
        let urlInfo: UrlData;
        try {
          urlInfo = await this.cms.getUploadUrl(uploadType, {
            fileName: file.name,
            parentId: parentObject?.artifact_id,
            fileSize: file.size,
          });
        } catch (e) {
          this.logger.error(`Getting url info failed: ${e}`);
        }
        if (!urlInfo) {
          break;
        }
        const mediaObject = urlInfo.multipart
          ? await this.uploadFileInParts(file, urlInfo, parentObject)
          : await this.uploadFile(file, urlInfo, parentObject, uploadType);
        if (mediaObject) {
          res.push(mediaObject);
        }
      }
      progressModal.close();
    }
    return res;
  }

  private async uploadFile(file: File,
                           urlInfo: UrlData,
                           parentObject: SuperObjectModel,
                           uploadType: UploadType): Promise<MediaItem> {
    let retry = false;
    let uploadFailCounter = 0;
    let mediaObject: any;
    do {
      retry = false;
      try {
        await this.putFile(file, urlInfo.url, uploadType);
        mediaObject = await this.getMediaObject(urlInfo.artifact_id, parentObject);
      } catch (e) {
        retry = await this.handleUploadError(e, uploadFailCounter, file);
        uploadFailCounter++;
      }
    } while (retry);
    return mediaObject;
  }

  /**
   * Multipart upload (P10-900). Parts are retried one by one inside uploadMultipart, so the whole file is
   * never sent again here: the part URLs expire, and a full restart would need a new order.
   */
  private async uploadFileInParts(file: File, urlInfo: UrlData, parentObject: SuperObjectModel): Promise<MediaItem> {
    try {
      await uploadMultipart(file, urlInfo.multipart, {
        putPart: (url, body) => fetch(url, {method: 'PUT', body}),
        complete: descriptor => this.cms.completeMultipartUpload(urlInfo.artifact_id, descriptor),
      });
      return await this.getMediaObject(urlInfo.artifact_id, parentObject);
    } catch (e) {
      this.logger.error(`Multipart upload failed: ${e}`);
      this.notificationService.addNotification(new AppNotification(
        ['TRANS__UPLOAD_SERVICE__UPLOAD_ERROR', ': ', file.name], 'error'));
      return undefined;
    }
  }

  private async getMediaObject(mediaId: string, parentObject: SuperObjectModel): Promise<MediaItem> {
    let mediaObject: any;
    mediaObject = await this.cms.getArtifact({artifact_id: mediaId} as GetArtifactParams);
    if (mediaObject.object_type === ObjectTypes.IMAGE) {
      mediaObject.media_publish_flag = true;
    }
    if (parentObject?.object_type === 'user' && mediaObject) {
      await this.handleUploadProfileImage(mediaObject as Image, parentObject as User);
    }
    mediaObject.$$uploading = true;
    return mediaObject;
  }

  // This new file uploader should be able to upload files using multipart upload¸ but needs to be triggered from a form
  async uploadFilesNew(files: File[], uploadType: UploadType, parentObject: SuperObjectModel): Promise<MediaItem[]> {
    return new Promise((resolve, reject) => {
      // Form-data to store files to send it
      // as a multipart/form-data post request
      const mediaObjects: MediaItem[] = [];
      // Inserting every file to formData
      let countDown = files.length;
      for (const file of files) {
        // Use the name as file
        // as mock backend configured
        // to get file as input in the name of file
        const formData = new FormData();
        formData.append('file', file);
        try {
          this.cms.getUploadUrl(uploadType, {
            fileName: file.name,
            parentId: parentObject?.artifact_id,
          }).then(urlInfo => {
            // Post method is returned for subscribing in the component
            this.http.put(urlInfo.url, formData).subscribe(res => {
              console.log('Response: ', res);
              this.getMediaObject(urlInfo.artifact_id, parentObject).then(mediaObject => {
                mediaObjects.push(mediaObject)
                countDown--;
                if (countDown === 0) {
                  resolve(mediaObjects);
                }
              })
            });
          })
        } catch (e) {
          reject(e);
        }
      }
    })
  }

  private async putFile(file: File, uploadUrl: string, uploadType: UploadType): Promise<Response> {
    // Send a concrete ArrayBuffer so the request body cannot become empty if the
    // File blob is revoked while fetch is in flight (seen on iOS Safari).
    const body = await readFileAsArrayBuffer(file);
    if (!body.byteLength) {
      throw new Error('EMPTY_FILE');
    }
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentTypeForUpload(file, uploadType)
      },
      body
    });
    if (!response.ok) {
      throw new Error(`UPLOAD_HTTP_${response.status}`);
    }
    return response;
  }

  private async handleUploadError(e: any, uploadFailCounter: number, file: File) {
    this.logger.error(`Upload failed ${e}`);
    const retry = uploadFailCounter < 10;
    if (!retry) {
      this.logger.warn('Tried uploading 10 times, giving up');
      this.notificationService.addNotification(new AppNotification(
        ['TRANS__UPLOAD_SERVICE__UPLOAD_ERROR', ': ', file.name], 'error'));
    } else {
      this.logger.warn(`Retrying upload for the ${uploadFailCounter} time`);
      // Wait a second before retrying
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
    return retry;
  }

  hasActiveUploadWorker(): boolean {
    return !!this.uploadWorker;
  }

  getLastUploadWorkerResult(): UploadResult {
    return this.lastUploadWorkerResult;
  }

  setUploadWorkerCallback(callback: any) {
    this.uploadWorkerCallback = callback;
  }

  getLastMediaFolder(): SuperObjectModel {
    return this.lastMediaFolder;
  }

  uploadFilesWithWorker(
    files: any,
    mediaFolder: SuperObjectModel,
    options: UploadOptions,
    callback: any,
  ) {
    this.lastMediaFolder = mediaFolder;
    const headers = this.cms.getHeaders();
    const uploadContainer = {
      files: files,
      folderId: mediaFolder.artifact_id,
      uploadOptions: options,
      uploadUrls: getUploadUrls(this.cms),
      completeMultipartUrl: this.cms.getApiUrl(this.cms.API_COMPLETE_MULTIPART_UPLOAD_URL, true),
      headers: Object.fromEntries(headers.keys().map(key => [key, headers.get(key)])),
      maxUploadAttempts: 5,
      maxFailedUploads: 5,
      texts: {
        statusOrderingUploadUrl: this.translate.instant('TRANS__UPLOAD_STATUS__ORDERING_UPLOAD_URL'),
        statusRetryOrderingUploadUrl: this.translate.instant('TRANS__UPLOAD_STATUS__RETRY_ORDERING_UPLOAD_URL'),
        statusOrderingUploadUrlFailed: this.translate.instant('TRANS__UPLOAD_STATUS__ORDERING_UPLOAD_URL_FAILED'),
        statusUploading: this.translate.instant('TRANS__UPLOAD_STATUS__UPLOADING'),
        statusUploadingFailed: this.translate.instant('TRANS__UPLOAD_STATUS__UPLOAD_FAILED'),
        statusRetryUploading: this.translate.instant('TRANS__UPLOAD_STATUS__RETRY_UPLOADING'),
        statusTooManyUploadsFailed: this.translate.instant('TRANS__UPLOAD_STATUS__TOO_MANY_UPLOADS_FAILED'),
        statusFileTooLarge: this.translate.instant('TRANS__EXCEPTION_MULTIMEDIA_FILE_TOO_LARGE'),
      }
    }
    this.uploadWorker = createUploadWorker();
    this.uploadWorker.postMessage(uploadContainer);
    this.uploadWorkerCallback = callback;
    this.uploadWorker.onmessage = ({data}) => {
      this.uploadWorkerCallback(data);
      this.checkFinishedAndTerminateWorker(data);
    }
    window.addEventListener('beforeunload', this.warnUnload);
    return this.uploadWorker;
  }

  private warnUnload = () => {
    return 'If you leave, the upload process will be terminated. Are you sure?';
  }

  private checkFinishedAndTerminateWorker(data: any) {
    this.lastUploadWorkerResult = data;
    if (data.uploadStatus === 'uploadFinished' || data.uploadStatus === 'failed') {
      this.logger.info('Terminating the upload worker')
      this.uploadWorker.terminate();
      this.uploadWorker = null;
      window.removeEventListener('beforeunload', this.warnUnload)
    }
  }

  private async handleUploadProfileImage(image: Image, user: User): Promise<void> {
    const userImageRelationItem: ImageItem = await this.cms.createArtifact({object_type: 'ImageItem'}) as ImageItem;
    if (!userImageRelationItem) {
      this.logger.error('Unable to create Image-User-relation:', userImageRelationItem);
      return;
    }

    const subArtifactList = new ContextList();

    if (user?.images?.length > 0) {
      user.images.filter(i => !!i.image_id).forEach(i => this.crud.setDestroy(i, true));
      subArtifactList.contexts.push(...user.images);
    }
    this.crud.setCreate(userImageRelationItem);
    userImageRelationItem.context_id = user.artifact_id;
    userImageRelationItem.image_id = image.artifact_id;

    subArtifactList.contexts.push(userImageRelationItem);

    await this.cms.saveSubArtifacts(subArtifactList);
  }

}
